/**
 * POSTLAUNCH-V6 · P1-#4 (Sa 06.09.2026) — Personen-Anker.
 *
 * Right-Rail-Widget zum Erfassen realer Personen (Name + Fokustyp),
 * inklusive Pro-Person-Log (chronologische Notizen mit Timestamp) und
 * Inline-Flexpfad-CTA (öffnet /anliegen mit vorausgefülltem Kontext).
 *
 * Speicher: LocalStorage (Key: `f4s_personen_v1`).
 * Architektur-Limit: keine Schema-Migration erlaubt → bewusst client-seitig.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus, Users, Plus, Trash2, ArrowRight, ChevronDown, ChevronUp, MessageSquare,
} from "lucide-react";
import { FokustypIcon } from "@/components/redesign/FokustypIcon";

const STORAGE_KEY = "f4s_personen_v1";
const FOKUS_OPTIONS = [
  { value: "hund", label: "Hund" },
  { value: "eule", label: "Eule" },
  { value: "katze", label: "Katze" },
  { value: "biene", label: "Biene" },
];

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function save(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function PersonCard({ person, onDelete, onAddNote }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const navigate = useNavigate();

  const submitNote = () => {
    const t = note.trim();
    if (!t) return;
    onAddNote(person.id, t);
    setNote("");
  };

  const openFlexpfad = () => {
    // Kontext-Vorausfüllung via URL-Param (DashboardAnliegen liest es einmalig ein)
    const params = new URLSearchParams({
      preset_name: person.name,
      preset_typ: person.fokustyp,
    });
    navigate(`/anliegen?${params.toString()}`);
  };

  return (
    <div
      className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-3"
      data-testid={`personen-item-${person.id}`}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex-shrink-0" aria-hidden="true">
          <FokustypIcon slug={person.fokustyp} size={28} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900 dark:text-white truncate" data-testid={`personen-item-name-${person.id}`}>
            {person.name}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Typ {FOKUS_OPTIONS.find((f) => f.value === person.fokustyp)?.label || "?"}
            {person.notes?.length > 0 && (
              <> · <b className="text-slate-700 dark:text-slate-200">{person.notes.length}</b> {person.notes.length === 1 ? "Notiz" : "Notizen"}</>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`personen-item-toggle-${person.id}`}
          aria-label={expanded ? "Einklappen" : "Ausklappen"}
          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => navigate(`/dashboard/fokustyp/${person.fokustyp}`)}
          data-testid={`personen-item-steckbrief-${person.id}`}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--fts-primary-bright)] hover:underline"
        >
          Steckbrief <ArrowRight className="w-2.5 h-2.5" />
        </button>
        <span className="text-slate-300 dark:text-slate-600" aria-hidden="true">·</span>
        <button
          type="button"
          onClick={openFlexpfad}
          data-testid={`personen-item-flexpfad-${person.id}`}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--fts-primary-bright)] hover:underline"
        >
          Flexpfad erstellen <ArrowRight className="w-2.5 h-2.5" />
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2" data-testid={`personen-item-log-${person.id}`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Notizen
          </p>
          {(person.notes || []).length === 0 && (
            <p className="text-[11px] italic text-slate-400 dark:text-slate-500">Noch keine Notiz.</p>
          )}
          <ul className="space-y-1.5 max-h-40 overflow-y-auto">
            {(person.notes || []).map((n) => (
              <li key={n.id} className="text-xs text-slate-700 dark:text-slate-200 leading-snug">
                <span className="text-[10px] text-slate-400 dark:text-slate-500 tabular-nums">
                  {fmtDate(n.at)}
                </span>{" "}
                — {n.text}
              </li>
            ))}
          </ul>
          <div className="flex items-start gap-1.5">
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid={`personen-item-note-input-${person.id}`}
              placeholder="Was ist dir aufgefallen?"
              className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)]/50"
            />
            <button
              type="button"
              onClick={submitNote}
              disabled={!note.trim()}
              data-testid={`personen-item-note-submit-${person.id}`}
              className="rounded-lg bg-[var(--fts-primary-bright)] hover:opacity-90 disabled:opacity-40 text-white p-1.5"
              aria-label="Notiz speichern"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => onDelete(person.id)}
            data-testid={`personen-item-delete-${person.id}`}
            className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-red-600 dark:hover:text-red-400"
          >
            <Trash2 className="w-3 h-3" /> Person löschen
          </button>
        </div>
      )}
    </div>
  );
}

export default function PersonenRail() {
  const [personen, setPersonen] = useState(load);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [typ, setTyp] = useState("hund");

  useEffect(() => save(personen), [personen]);

  const add = () => {
    const t = name.trim();
    if (!t) return;
    const p = {
      id: `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
      name: t,
      fokustyp: typ,
      created_at: new Date().toISOString(),
      notes: [],
    };
    setPersonen((prev) => [p, ...prev]);
    setName("");
    setTyp("hund");
    setShowForm(false);
  };

  const del = (id) => setPersonen((prev) => prev.filter((p) => p.id !== id));

  const addNote = (personId, text) => {
    setPersonen((prev) =>
      prev.map((p) =>
        p.id === personId
          ? {
              ...p,
              notes: [
                { id: `n_${Date.now().toString(36)}`, at: new Date().toISOString(), text },
                ...(p.notes || []),
              ],
            }
          : p
      )
    );
  };

  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5"
      data-testid="dashboard-right-personen"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 text-[var(--fts-primary-bright)]" aria-hidden="true" />
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Wen hast du im Kopf?
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          data-testid="personen-toggle-form"
          aria-label={showForm ? "Formular schließen" : "Person hinzufügen"}
          className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-[var(--fts-primary-bright)]"
        >
          {showForm ? <ChevronUp className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
        </button>
      </div>

      {showForm && (
        <div className="mb-3 space-y-2" data-testid="personen-form">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (z.B. Mein Bruder)"
            data-testid="personen-form-name"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)]/50"
          />
          <select
            value={typ}
            onChange={(e) => setTyp(e.target.value)}
            data-testid="personen-form-typ"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)]/50"
          >
            {FOKUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={add}
            disabled={!name.trim()}
            data-testid="personen-form-submit"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-[var(--fts-primary-bright)] hover:opacity-90 disabled:opacity-40 text-white text-sm font-bold px-3 py-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Hinzufügen
          </button>
        </div>
      )}

      {personen.length === 0 ? (
        <p
          className="text-xs italic text-slate-500 dark:text-slate-400 leading-snug"
          data-testid="personen-empty"
        >
          {/* Issue #11 (Pre-Launch Do10.09.2026, 15:30) — einladender formuliert */}
          <b className="not-italic text-slate-700 dark:text-slate-200">Die Menschen aus deinem Leben.</b>{" "}
          Hier kannst du für bestimmte Menschen aus deinem Leben ein Profil erstellen, das du jederzeit bearbeiten kannst.
        </p>
      ) : (
        <div className="space-y-2" data-testid="personen-liste">
          {personen.map((p) => (
            <PersonCard key={p.id} person={p} onDelete={del} onAddNote={addNote} />
          ))}
        </div>
      )}
    </section>
  );
}
